import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Menu, X, ArrowRight } from 'lucide-react';
import { NAV } from '../data/site';

const V2Nav = () => {
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobile(false);
    setOpen(null);
  }, [location.pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      {/* announcement bar */}
      <div className="bg-[#0a1633] px-4 py-2 text-center text-[13px] text-white/90">
        <span className="v2-mono mr-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9db4ff]">New</span>
        Asha AI Scribe — clinical notes that write themselves.{' '}
        <Link to="/v2/clinical-ai" className="inline-flex items-center gap-1 font-bold text-white underline underline-offset-2 hover:text-[#9db4ff]">
          See it live <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      <div
        className={`transition-all duration-300 ${
          scrolled
            ? 'border-b border-[#dde5f3] bg-white/90 shadow-[0_1px_16px_rgba(10,22,51,0.06)] backdrop-blur-xl'
            : 'border-b border-transparent bg-white'
        }`}
      >
        <nav className="mx-auto flex h-[68px] max-w-7xl items-center gap-8 px-6">
          <Link to="/v2" className="flex items-center gap-2.5">
            <img src="/assets/alogo.png" alt="Asha" className="h-8 w-8 rounded-lg" />
            <span className="v2-display text-[24px] font-semibold tracking-tight text-[#0a1633]">asha</span>
          </Link>

          {/* desktop */}
          <div className="hidden items-center gap-1 lg:flex" onMouseLeave={() => setOpen(null)}>
            {NAV.map((item) =>
              item.items ? (
                <div key={item.label} className="relative" onMouseEnter={() => setOpen(item.label)}>
                  <button className="flex items-center gap-1 rounded-lg px-4 py-2 text-[14.5px] font-semibold text-[#3f4d6e] transition-colors hover:text-[#1a46e5]">
                    {item.label}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open === item.label ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {open === item.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.16 }}
                        className="absolute left-0 top-full w-[22rem] pt-2"
                      >
                        <div className="overflow-hidden rounded-2xl border border-[#dde5f3] bg-white p-2 shadow-[0_28px_64px_-20px_rgba(10,22,51,0.22)]">
                          {item.items.map((sub, i) => (
                            <Link
                              key={sub.label}
                              to={sub.to}
                              className="group flex items-start gap-3.5 rounded-xl px-4 py-3 transition-colors hover:bg-[#f6f9ff]"
                            >
                              <span className="v2-mono mt-0.5 text-[11px] font-medium text-[#a8bcf0] group-hover:text-[#1a46e5]">
                                {String(i + 1).padStart(2, '0')}
                              </span>
                              <span className="flex flex-col gap-0.5">
                                <span className="flex items-center gap-1.5 text-[14.5px] font-bold text-[#0a1633] group-hover:text-[#1a46e5]">
                                  {sub.label}
                                  <ArrowRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                                </span>
                                <span className="text-[13px] text-[#7c89a8]">{sub.desc}</span>
                              </span>
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  className="rounded-lg px-4 py-2 text-[14.5px] font-semibold text-[#3f4d6e] transition-colors hover:text-[#1a46e5]"
                >
                  {item.label}
                </Link>
              )
            )}
          </div>

          <div className="ml-auto hidden items-center gap-3 lg:flex">
            <Link
              to="/contact"
              className="rounded-full border border-[#c4d2ec] px-5 py-2.5 text-[14px] font-semibold text-[#0a1633] transition-colors hover:border-[#1a46e5] hover:text-[#1a46e5]"
            >
              Contact
            </Link>
            <Link
              to="/book-demo"
              className="rounded-full bg-[#1a46e5] px-5 py-2.5 text-[14px] font-bold text-white shadow-[0_8px_22px_-8px_rgba(26,70,229,0.6)] transition-all hover:-translate-y-px hover:bg-[#1236bd]"
            >
              Get a demo
            </Link>
          </div>

          <button className="ml-auto text-[#0a1633] lg:hidden" onClick={() => setMobile(!mobile)} aria-label="Menu">
            {mobile ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </nav>

        {/* mobile */}
        <AnimatePresence>
          {mobile && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden border-t border-[#dde5f3] bg-white lg:hidden"
            >
              <div className="space-y-4 px-6 py-5">
                {NAV.map((item) => (
                  <div key={item.label}>
                    <div className="v2-mono mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7c89a8]">
                      {item.label}
                    </div>
                    {(item.items || [{ label: item.label, to: item.to }]).map((sub) => (
                      <Link
                        key={sub.label}
                        to={sub.to}
                        className="block rounded-lg px-3 py-2.5 text-[15px] font-semibold text-[#0a1633] hover:bg-[#f6f9ff]"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                ))}
                <Link
                  to="/book-demo"
                  className="mt-2 block rounded-full bg-[#1a46e5] px-5 py-3 text-center text-[15px] font-bold text-white"
                >
                  Get a demo
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export default V2Nav;
